/**
 * serverTaskResume
 *
 * Point 6 (Option B) — AUTHENTICATED-path refresh-survival for in-flight
 * server-side durable turns.
 *
 * Background
 * ──────────────────────────────────────────────────────────────────────
 * On the authenticated / account-VM path a turn runs as a durable server-side
 * background task: `usePrest0nSession.sendTurn` enqueues it (onTaskQueued) and
 * polls `getPrest0nTaskStatus` (cloud function `prest0nGetTaskStatus`). The
 * GUEST path already survives a hard refresh — the in-flight message carries a
 * `serverTaskId`, `findResumableServerTasks()` collects it on hydrate, and
 * `resumeGuestServerTask()` re-polls the GUEST endpoint and finalizes.
 *
 * This module is the AUTHED counterpart's PURE core: given the latest
 * authenticated `Prest0nTaskRecord` (or null), decide how to finalize the
 * re-attached in-flight message. Keeping the decision pure (no React, no
 * polling, no Firestore) makes it trivially unit-testable — mirroring how
 * `interruptedTurn.ts` is pure/testable. The host hook (`useGlobalChat`) owns
 * the polling loop and the actual `finalizeStreamingMessageById` call.
 * (2026-07-06: now imports one pure helper from the leaf codename config —
 * still no React/polling/Firestore, so unit-testability is unchanged.)
 */

import { withoutDocSourceAttachments } from '../../utils/docSourceAttachments';
import { detectProviderFromModelId } from '../../config/providerCodeNames';
import { withOncallNotice } from '../../config/oncallNotice';
import { buildAttachmentsReadyNotice, isStepLimitNarration } from './stepLimitNarration';
import {
  isClarifyingSkipBreadcrumbBody,
  resolveLatestDeepResearchPath,
} from '../../services/prest0nSession/clarifyingBreadcrumb';

/** Usage shape carried on completed task records (kept inline — pure module). */
export interface AuthedTaskUsageLike {
  inputTokens?: number;
  outputTokens?: number;
  reasoningTokens?: number;
  model?: string;
  provider?: string;
  upstreamCostUsd?: number;
  /**
   * Per-model generation breakdown as the backend TurnResult actually
   * persists it (functions/src/prest0nSession/types.ts usage.allGenerations).
   * Task docs never carry a top-level result.tokenUsages — that field exists
   * only on the live SSE done event — so multi-model recovery must derive
   * from this.
   */
  allGenerations?: Array<{
    model?: string;
    nativeInputTokens?: number;
    nativeOutputTokens?: number;
    nativeReasoningTokens?: number;
    nativeTotalCost?: number;
    billedSeparately?: boolean;
    stage?: string;
    costSource?: string;
  }>;
  /** Lane/stage/source flags of a breakdown row (see TokenUsageMetadata). */
  billedSeparately?: boolean;
  stage?: string;
  costSource?: string;
}

/** Minimal shape of the authenticated task record this module reasons about. */
export interface AuthedTaskResultLike {
  finalResponse?: string;
  surfaceOwnedText?: string;
  error?: string;
  attachmentsSerialized?: Array<{
    name: string;
    type: string;
    size: number;
    storageUrl?: string;
    data?: string;
  }>;
  usage?: AuthedTaskUsageLike;
  /** Per-model breakdown when multiple ladder models served the turn. */
  tokenUsages?: AuthedTaskUsageLike[];
  /**
   * INTERSTITIAL-MESSAGE ELIMINATION (2026-08-13): tool results as the task
   * doc persists them (full shape normally; toolName + metadata.path/status
   * on the minimal floor payload). Read ONLY to recognize a completed
   * clarifying-questions turn so the resume never paints the skip breadcrumb
   * or an Interrupted card over a healthy clarifying card.
   */
  toolsInvoked?: Array<{
    toolName?: string;
    metadata?: { path?: unknown } | Record<string, unknown> | null;
  } | null>;
}

export interface AuthedTaskRecordLike {
  status: 'pending' | 'running' | 'completed' | 'failed';
  error?: string;
  /**
   * F1 (2026-08-03): terminal disposition code the backend stamps alongside
   * status ('cancelled_by_user' for a user Stop — see taskQueue
   * CANCELLED_BY_USER_ERROR_CODE). Drives the silent 'cancelled' decision.
   */
  errorCode?: string;
  /**
   * 2026-06 reliability overhaul — structured failure info the backend now
   * stamps on failed task docs alongside the legacy raw `error` string. The
   * raw string can be a JSON/stack dump (`Error: {"code":"FERN-ROUTE-005",...}`
   * + stack) and MUST NOT be rendered into the Interrupted card anymore;
   * `userMessage` (+ traceId for support lookups) is the chat-safe surface.
   * Shape mirrors prest0nSession/types.ts TaskErrorInfo — duplicated inline
   * so this module stays import-free/pure (see module docblock).
   */
  errorInfo?: {
    code: string;
    traceId: string;
    userMessage: string;
    userMessageEs: string;
  };
  result?: AuthedTaskResultLike;
}

/**
 * Extract the per-model usage breakdown from a completed task record.
 * Prefers an explicit result.tokenUsages (live-SSE shape, future-proofing);
 * otherwise derives it from usage.allGenerations — the shape the backend
 * actually persists on task docs. Without this derivation the multi-model
 * popup sections never render on any recovery/polling path.
 */
export function extractTaskTokenUsages(
  result: AuthedTaskResultLike | null | undefined,
): AuthedTaskUsageLike[] | undefined {
  if (!result) return undefined;
  if (Array.isArray(result.tokenUsages) && result.tokenUsages.length > 0) {
    return result.tokenUsages;
  }
  const generations = result.usage?.allGenerations;
  if (!Array.isArray(generations) || generations.length === 0) return undefined;
  const mapped = generations
    .filter((g) => g && typeof g === 'object')
    .map((g) => ({
      inputTokens: typeof g.nativeInputTokens === 'number' ? g.nativeInputTokens : undefined,
      outputTokens: typeof g.nativeOutputTokens === 'number' ? g.nativeOutputTokens : undefined,
      reasoningTokens:
        typeof g.nativeReasoningTokens === 'number' && g.nativeReasoningTokens > 0
          ? g.nativeReasoningTokens
          : undefined,
      model: g.model,
      provider: detectProviderFromModelId(g.model),
      upstreamCostUsd:
        typeof g.nativeTotalCost === 'number' && g.nativeTotalCost > 0
          ? g.nativeTotalCost
          : undefined,
      // 2026-09-08: the async-task lane (every video render rides it) used to DROP the lane
      // flag, the stage and the cost source here — the ⓘ popup could neither label a
      // vendor-billed step nor mark a unit-priced estimate on a resumed task.
      ...(g.billedSeparately === true ? { billedSeparately: true } : {}),
      ...(typeof g.stage === 'string' && g.stage ? { stage: g.stage } : {}),
      ...(g.costSource === 'provider' || g.costSource === 'estimate' ? { costSource: g.costSource } : {}),
    }));
  return mapped.length > 0 ? mapped : undefined;
}

/**
 * Default interrupted-notice copy when a server task is gone / failed.
 * Reworded (session-hardening S1): this card fires on TASK-not-found, and the
 * old "session expired" copy sent owners hunting session bugs that didn't
 * exist. It also only renders after the miss-streak + min-age gate now.
 */
// copy: interrupted_turn
export const SERVER_TASK_NOT_FOUND_NOTICE =
  'This reply could no longer be found on the server. Tap Regenerate to run it again. This has been reported to the Prest0 on-call team.';
// copy: interrupted_turn
export const SERVER_TASK_FAILED_NOTICE =
  'This reply could not be completed. Tap Regenerate to run it again. This has been reported to the Prest0 on-call team.';

/**
 * The two terminal outcomes for an authed resume. `completed` carries the
 * finalized answer body + any deliverables; `interrupted` carries the calm
 * notice + the regenerate prompt so the host can render the recoverable card.
 */
export type AuthedResumeDecision =
  | {
      kind: 'completed';
      finalResponse: string;
      attachmentsSerialized?: AuthedTaskResultLike['attachmentsSerialized'];
      /**
       * Usage from the completed task record (2026-07-06). Previously dropped
       * on every refresh-recovery finalize, so recovered billable turns showed
       * the "no extensive compute was necessary" popup while the wallet was
       * debited. Hosts must attach this to the finalized message's tokenUsage.
       */
      usage?: AuthedTaskUsageLike;
      tokenUsages?: AuthedTaskUsageLike[];
    }
  | {
      kind: 'interrupted';
      notice: string;
    }
  | {
      /**
       * F1 (2026-08-03): the task terminalized because the USER stopped it
       * (errorCode 'cancelled_by_user'). The Stop already rendered its own
       * "stopped" chip — the resume path must clear the in-flight widget
       * WITHOUT painting an Interrupted card over the user's own action
       * (mirrors the silent terminal in usePrest0nSession's live poll).
       */
      kind: 'cancelled';
    }
  | {
      /**
       * EMPTY-DELIVERY RETRY HANDOFF (2026-08-05, owner repro "3D warehouse
       * apartment" on the Fernando VM lane): the server quietly terminalized
       * this doc as failed/TASK-EMPTY-DELIVERY the moment it enqueued its ONE
       * silent salvage retry — the failure is NOT terminal for the user yet.
       * The caller must re-point polling at `retryTaskId` and keep the
       * in-progress surface; the retry's own terminal state decides what the
       * user finally sees.
       */
      kind: 'retrying';
      retryTaskId: string;
      /**
       * WHY the chain is being followed — the caller picks its waiting copy
       * from this, and the two cases are nothing alike.
       *
       * 'rescue' is a salvage retry: one more pass at a turn that produced
       * nothing, seconds away from an answer either way.
       *
       * 'doc-continue' is a LONG DOCUMENT still being written, and it can run
       * for tens of minutes across up to MAX_DOC_CONTINUE_HOPS fresh task
       * envelopes. Before 2026-09-08 both showed "almost there — taking one
       * more pass". On a document chain that sentence is not merely vague, it
       * is FALSE: it promises seconds and can mean another half hour. Raising
       * the hop ceiling from 2 to 12 turned that from a small inaccuracy into
       * the main thing a waiting user is told.
       */
      reason: 'rescue' | 'doc-continue';
    }
  | {
      /**
       * INTERSTITIAL-MESSAGE ELIMINATION (owner directive 2026-08-13): the
       * task completed as a deep-research INTERACTIVE CLARIFYING turn — the
       * structured card is its only legitimate surface. The old behavior
       * finalized the placeholder with `surfaceOwnedText`, which on these
       * turns was the one-line skip breadcrumb ("Clarifying questions ready —
       * tap **Skip to Deep Research** …"), materializing a phantom assistant
       * bubble that ALSO absorbed the turn's token usage while the real card
       * showed "no extensive compute was necessary".
       *
       * The host must rebuild the card from the task record's
       * currentStage.payload (persisted server-side exactly for this) and
       * attach `usage`/`tokenUsages` to it — or, when no payload survived,
       * clear the in-flight placeholder silently. NEVER paint the breadcrumb
       * or an Interrupted card for this decision.
       */
      kind: 'clarifying';
      usage?: AuthedTaskUsageLike;
      tokenUsages?: AuthedTaskUsageLike[];
    };

/**
 * Pure decision: given the latest authenticated task record (or null when the
 * task could not be fetched / does not exist), decide how to finalize the
 * re-attached in-flight message.
 *
 *  - `completed` + a non-empty `finalResponse` → finalize with the answer.
 *  - `completed` + deliverables/surface text    → finalize as completed.
 *  - `completed` but truly empty                → treat as failed (nothing to show).
 *  - `failed`                                   → interrupted (use task error text).
 *  - null (not found / fetch failed)            → interrupted (not-found notice).
 *  - still pending/running                      → null (caller keeps polling).
 *
 * Returns `null` ONLY for a non-terminal status, signalling "keep polling".
 *
 * `opts.language` (additive, 2026-06): lets the caller pick the Spanish
 * `errorInfo.userMessageEs` variant for failed tasks. The host detects the
 * language (e.g. from the regenerate prompt) so this module stays pure.
 */
export function decideAuthedResumeFinalize(
  record: AuthedTaskRecordLike | null | undefined,
  opts?: { language?: 'en' | 'es' },
): AuthedResumeDecision | null {
  // No record at all → the task is gone / could not be fetched. Interrupted.
  if (!record) {
    return { kind: 'interrupted', notice: SERVER_TASK_NOT_FOUND_NOTICE };
  }

  if (record.status === 'completed') {
    const result = record.result;
    const finalResponse = (result?.finalResponse || '').trim();
    const surfaceOwnedText = (result?.surfaceOwnedText || '').trim();
    // WS-0 0.6: shared detector (this inline regex had drifted from the
    // server persist gate's copy — it was missing the zero-tools clause).
    const stepLimitNarration = isStepLimitNarration(finalResponse);
    // The server strips the canonical-source sibling at its persist
    // chokepoint, so a task completed from here on never carries one. A task
    // document written BEFORE that still does, and this is the field the
    // browser polls — the exact path the Test 16 retest chips came through.
    const rawAttachments = withoutDocSourceAttachments(result?.attachmentsSerialized);
    const attachmentsSerialized =
      Array.isArray(rawAttachments) &&
      rawAttachments.length > 0
        ? rawAttachments
        : undefined;
    const usage = result?.usage && typeof result.usage === 'object' ? result.usage : undefined;
    const tokenUsages = extractTaskTokenUsages(result);
    // INTERSTITIAL-MESSAGE ELIMINATION (2026-08-13): a completed
    // STRUCTURED-CARD clarifying turn never finalizes into a text bubble.
    // Detect via the persisted deep-research tool path (post-fix servers ship
    // no surfaceOwnedText for these turns, so text alone can't identify them)
    // OR via breadcrumb-shaped body text (task docs written by PRE-FIX
    // servers). Scope notes:
    //   - ONLY path 'clarifying-questions' qualifies — that is the one path
    //     whose surface is the SSE/stage-payload card with a deliberately
    //     blank finalResponse. 'fast-clarify' renders a REAL markdown card
    //     through finalResponse and must keep resuming as ordinary completed
    //     text.
    //   - Latest-wins path resolution: a leading failed deep-research
    //     invocation (no metadata) must never mask the retry that produced
    //     the card (adversarial-review finding, 2026-08-13).
    //   - Attachment-bearing results are never clarifying turns.
    const drPath = resolveLatestDeepResearchPath(result?.toolsInvoked);
    const isClarifyingTurn =
      !attachmentsSerialized &&
      (drPath === 'clarifying-questions' ||
        isClarifyingSkipBreadcrumbBody(surfaceOwnedText) ||
        isClarifyingSkipBreadcrumbBody(finalResponse));
    if (isClarifyingTurn) {
      return { kind: 'clarifying', usage, tokenUsages };
    }
    if (finalResponse && !(stepLimitNarration && (surfaceOwnedText || attachmentsSerialized))) {
      return {
        kind: 'completed',
        finalResponse,
        attachmentsSerialized,
        usage,
        tokenUsages,
      };
    }
    // Surface-owned completions can intentionally blank finalResponse while
    // carrying body/deliverables elsewhere. WS-0 0.6: when deliverables
    // exist but no body text survived, render the attachments-ready notice
    // instead of an empty bubble above the download cards.
    if (surfaceOwnedText || attachmentsSerialized) {
      return {
        kind: 'completed',
        finalResponse: surfaceOwnedText
          || buildAttachmentsReadyNotice(
            (attachmentsSerialized || []).map((att) => String(att?.name || '')).filter(Boolean),
            opts?.language,
          ),
        attachmentsSerialized,
        usage,
        tokenUsages,
      };
    }

    // Completed but produced no visible answer body/deliverables → nothing to re-attach;
    // surface the recoverable card so the user can re-run.
    return { kind: 'interrupted', notice: SERVER_TASK_FAILED_NOTICE };
  }

  if (record.status === 'failed') {
    // F1 (2026-08-03): a task the user STOPPED terminalizes as
    // failed/cancelled_by_user. Rendering the Interrupted card here painted
    // "could not be completed" over the user's own Stop after a refresh —
    // clear the widget silently instead.
    if (record.errorCode === 'cancelled_by_user') {
      return { kind: 'cancelled' };
    }
    // RESCUE-RETRY HANDOFF (2026-08-05 empty-delivery; generalized
    // 2026-08-11 for vm-transport + the watchdog deploy-orphan retry): a
    // quiet terminalization stamped while a server salvage retry is (or was)
    // running. Never render Interrupted off THIS doc — hand the caller the
    // retry id so the widget follows the live salvage instead.
    const rescueMeta = (record as unknown as {
      dispatchMetadata?: {
        emptyDeliveryRetriedBy?: unknown;
        vmTransportRetriedBy?: unknown;
        watchdogRetriedBy?: unknown;
        docContinuedBy?: unknown;
        intakeContinuedBy?: unknown;
      };
    })?.dispatchMetadata;
    // DOC-COMPLETENESS (2026-09-01): a cut document quietly handed to a
    // doc-continue hop — follow the hop like the other rescue chains so the
    // widget shows the live continuation, never an Interrupted card.
    //
    // Checked SEPARATELY from the salvage pointers (2026-09-08) because the
    // caller's waiting copy depends on which one it is: a salvage retry is
    // seconds, a document chain can be tens of minutes.
    //
    // INTAKE-COMPLETENESS (2026-09-11): reading a packet can run out of
    // envelope the same way writing a document can, and it hands off the same
    // way. It belongs with the doc chain rather than with the salvage
    // pointers, for the reason stated just above: the waiting copy differs,
    // and a continuation is long where a salvage is seconds.
    const continued = [rescueMeta?.docContinuedBy, rescueMeta?.intakeContinuedBy]
      .find((v): v is string => typeof v === 'string' && !!v.trim());
    if (continued) {
      return { kind: 'retrying', retryTaskId: continued.trim(), reason: 'doc-continue' };
    }
    const retriedBy = [
      rescueMeta?.emptyDeliveryRetriedBy,
      rescueMeta?.vmTransportRetriedBy,
      rescueMeta?.watchdogRetriedBy,
    ].find((v): v is string => typeof v === 'string' && !!v.trim());
    if (retriedBy) {
      return { kind: 'retrying', retryTaskId: retriedBy.trim(), reason: 'rescue' };
    }
    // 2026-06 reliability overhaul: prefer the structured human-readable
    // failure message (+ traceId so support can correlate logs). The legacy
    // raw `record.error` string is no longer rendered as-is — it dumped
    // `Error: {"code":"FERN-ROUTE-005",...}` + stack straight into the
    // Interrupted card. It survives only as a SANITIZED fallback for old
    // task docs without errorInfo: short human prose passes the raw-dump
    // filter, JSON/stack/code payloads collapse to the calm generic notice.
    // The Regenerate affordance is unchanged either way.
    const info = record.errorInfo;
    const human = (
      (opts?.language === 'es' ? info?.userMessageEs : undefined)
      || info?.userMessage
      || ''
    ).trim();
    // Owner directive 2026-08-13: server-built userMessage already carries the
    // on-call notice (fernandoStrict appends centrally); withOncallNotice covers
    // the sanitized-legacy lane and skips copy that already states it.
    const notice = human
      ? `${withOncallNotice(human)} (ref: ${info!.traceId})`
      : withOncallNotice(sanitizeServerErrorNotice(record.error || record.result?.error) || SERVER_TASK_FAILED_NOTICE);
    return { kind: 'interrupted', notice };
  }

  // pending / running → not terminal yet. Caller keeps polling.
  return null;
}

/**
 * 2026-06 reliability overhaul — last-line filter for server-provided failure
 * strings on paths that have NO structured errorInfo (e.g. the guest research
 * poll). Lets short human prose through (like the client-built "Research
 * timed out…" notice) but blocks anything that smells like a raw technical
 * dump — JSON braces/brackets, stack-frame lines, or FERN-style error codes —
 * so a backend error string can never be rendered into a chat notice again.
 * Returns the trimmed message when it looks like prose, '' otherwise (callers
 * fall back to their calm generic notice).
 */
export function sanitizeServerErrorNotice(raw: string | null | undefined): string {
  const text = (raw || '').trim();
  if (!text) return '';
  if (text.length > 240) return ''; // real notices are short; dumps are not
  if (/[{}[\]]/.test(text)) return ''; // JSON-ish payload
  if (/[<>]/.test(text)) return ''; // markup — some callers interpolate into html
  if (/\bat .+:\d+/.test(text)) return ''; // stack-frame line ("at fn (file:12:3)")
  if (/\b[A-Z]{2,}(?:-[A-Z0-9]+){1,}-\d+\b/.test(text)) return ''; // FERN-ROUTE-005-style codes
  return text;
}

/**
 * Build the calm, NON-animated interrupted-notice html for an authed resume
 * that genuinely failed / expired. Mirrors the guest resume fallback markup so
 * the renderer treats it as a normal recoverable bubble (NEVER a live spinner —
 * it contains neither pendingShimmer nor pendingSpin).
 */
export function buildAuthedInterruptedHtml(notice: string, promptPreview?: string): string {
  const safeNotice = (notice || SERVER_TASK_FAILED_NOTICE)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const dataPrompt = promptPreview
    ? ` data-regenerate-prompt="${promptPreview.replace(/&/g, '&amp;').replace(/"/g, '&quot;')}"`
    : '';
  return (
    `<div class="interrupted-turn-notice" role="status" data-interrupted="true"${dataPrompt} ` +
    `style="display:flex;flex-direction:column;gap:4px;padding:12px 16px;border-radius:12px;` +
    `background:#FBF7EE;border:1px solid rgba(198,167,94,0.25);font-size:13px;color:#8a6d2f;">` +
    `<span style="font-weight:600;">Interrupted</span><span>${safeNotice}</span></div>`
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Auth-init race gate (Point 6 refresh-survival regression fix)
// ──────────────────────────────────────────────────────────────────────────
//
// On a HARD REFRESH, Firebase Auth has not rehydrated yet, so the reactive
// `chatAccountId` is transiently `null` — INDISTINGUISHABLE from a genuine
// logged-out guest (also null). If the recovery effects treat that transient
// null as "guest", the guest resume path polls the GUEST endpoint for an
// AUTHENTICATED task id, fails to find it, and finalizes the in-flight message
// as interrupted ("This research session expired or could not be found") —
// showing authenticated users the guest interrupted card.
//
// Fix (mirrors commit #281's auth-ready gating): the host hook tracks an
// `authHasResolved` signal (onAuthStateChanged fired at least once). This pure
// helper centralizes the three-way decision so it can be unit-tested:
//   • auth not resolved yet (or gate still checking) → 'wait'  (NEITHER path runs)
//   • auth resolved, no accountId                    → 'guest'
//   • auth resolved, accountId present               → 'authed'
//
// 'wait' is the regression guard: while auth is mid-rehydration the in-flight
// message stays in its pending/streaming state (spinner) instead of being
// finalized as interrupted by the premature guest path. Once auth settles,
// exactly one of guest/authed fires and re-attaches to the live server task.

export type ResumeSurfaceMode = 'wait' | 'guest' | 'authed';

export interface SelectResumeModeInput {
  /** True once onAuthStateChanged has fired at least once (uid may be null=guest or a string=authed). */
  authHasResolved: boolean;
  /** True while the async migration gate is still resolving guest-vs-migrated. */
  isMigrationGateChecking: boolean;
  /** Canonical migrated/account id once the gate resolves; null on the guest surface. */
  prest0nSessionAccountId: string | null;
}

export interface ShouldSkipLiveTaskResumeInput {
  /** Candidate server task id discovered from an in-flight message. */
  serverTaskId: string | null | undefined;
  /** Task id currently being actively driven by this tab's session hook. */
  activeTaskId: string | null | undefined;
  /** True while the hook is polling an async server task. */
  isPollingTask: boolean | undefined;
  /** True while the hook is handling a live stream turn. */
  isStreaming: boolean | undefined;
  /**
   * True when this tab's live-send registry holds the task id (registered at
   * mint, cleared when sendTurn settles). STRUCTURAL race guard (session-
   * hardening S1, Miles 2026-07-12): the pre-flight stamp lands on the widget
   * BEFORE sendTurn flips `isStreaming` — in a fresh thread the session-create
   * round-trip holds that window open for seconds, and the resume effect used
   * to hijack the live turn inside it (the false "session expired" card).
   */
  isRegisteredLiveSend?: boolean;
}

/**
 * Decide which refresh-survival resume path may run on the current surface.
 *
 * Pure: no React, no auth, no polling. The host effects call this and early
 * return on anything other than their own mode.
 */
export function selectResumeMode(input: SelectResumeModeInput): ResumeSurfaceMode {
  // Auth still mid-rehydration → ambiguous null. Do NOT let the guest path
  // claim an authenticated task. Wait for auth to resolve first.
  if (!input.authHasResolved) return 'wait';
  // Migration gate still resolving guest-vs-migrated → not yet decidable.
  if (input.isMigrationGateChecking) return 'wait';
  // Auth resolved + gate settled: a present account id is the authed surface,
  // a null one is a genuine logged-out guest.
  return input.prest0nSessionAccountId ? 'authed' : 'guest';
}

/**
 * Guard for refresh-survival resume effects: never start a second resume/poll
 * loop for a task this tab is already driving live.
 */
export function shouldSkipLiveTaskResume(input: ShouldSkipLiveTaskResumeInput): boolean {
  const taskId = (input.serverTaskId || '').trim();
  if (!taskId) return true;
  if (input.isRegisteredLiveSend) return true;
  if (input.isPollingTask || input.isStreaming) return true;
  return taskId === (input.activeTaskId || '').trim();
}

// ──────────────────────────────────────────────────────────────────────────
// Session-hardening S1 (Miles 2026-07-12): not-found is terminal only when
// the task is provably old enough to exist AND repeatedly absent.
// ──────────────────────────────────────────────────────────────────────────

/**
 * Recover the mint timestamp encoded in a client task id
 * (`task_<base36 epoch ms>_<rand>`, see mintClientTaskId / the server's
 * generateTaskId — both encode Date.now().toString(36)). Returns null for
 * legacy/foreign ids, which callers must treat as "age unknown" (old enough).
 */
export function taskMintTimestampMs(taskId: string | null | undefined): number | null {
  const raw = (taskId || '').trim();
  const match = /^task_([0-9a-z]+)_/.exec(raw);
  if (!match) return null;
  const ts = parseInt(match[1], 36);
  if (!Number.isFinite(ts) || ts <= 0) return null;
  // Sanity window: base36 garbage can parse to absurd values. Accept only
  // plausible epoch millis (2020-01-01 .. now + 1 day handled by caller).
  if (ts < 1_577_836_800_000) return null;
  return ts;
}

/** Consecutive not-found probes required before a missing task is terminal. */
export const TASK_MISSING_TERMINAL_MISSES = 3;
/** Minimum age before a missing task can be terminal (enqueue latency cover). */
export const TASK_MISSING_MIN_AGE_MS = 60_000;

/**
 * Decide whether a missing task record (fetch returned null) may be treated
 * as TERMINAL not-found. Pure. The old behavior — first null is terminal —
 * raced the live send: the pre-flight stamp exists before the server task
 * doc does, and a single 404/network blip painted the "session expired"
 * card over a healthy in-flight turn.
 *
 * A null verdict here means "keep polling" — the caller's wall-clock bound
 * (and the server's stale-task sweep flipping dead tasks to `failed`) remain
 * the authorities that end a genuinely dead resume.
 *
 * `mintTsMs === null` (legacy/foreign id) counts as old enough — those ids
 * cannot be younger than the code that minted them, and requiring the miss
 * streak alone preserves refresh-recovery for pre-fix tasks.
 */
export function shouldTreatMissingTaskAsTerminal(input: {
  consecutiveMisses: number;
  mintTsMs: number | null;
  nowMs: number;
}): boolean {
  if (input.consecutiveMisses < TASK_MISSING_TERMINAL_MISSES) return false;
  if (input.mintTsMs === null) return true;
  return input.nowMs - input.mintTsMs >= TASK_MISSING_MIN_AGE_MS;
}

// ──────────────────────────────────────────────────────────────────────────
// Phase 1.5 — resume DISCOVERY plan (all-threads re-attach, RC1.4)
// ──────────────────────────────────────────────────────────────────────────
// The per-message serverTaskId resume path only scans the ACTIVE thread's
// messages, so a BACKGROUND thread with a still-running server task never
// re-lights its chip on hydrate/second-device until the user opens it. The
// discovery endpoint (prest0nListActiveTasks) returns the account's
// SERVER-CONFIRMED non-terminal tasks; this pure planner turns that list into
// concrete client actions, so a chip is lit only for a task the server says is
// genuinely running — never from a stale local pending placeholder.

/** Minimal message shape this planner needs (keeps the module React-free). */
export interface ResumeScanMessage {
  id?: string;
  isUser?: boolean;
  status?: string;
  interrupted?: boolean;
  serverTaskId?: string;
  text?: string;
  html?: string;
}

export interface ActiveTaskLike {
  taskId: string;
  threadId: string | null;
  /** When the server first enqueued this task (epoch ms), when it reports it. */
  createdAtMs?: number | null;
}

export interface ActiveTaskReattachPlan {
  /** Threads to light a processing chip for (server-confirmed running). */
  chipThreadIds: string[];
  /**
   * Per-thread turn clock (epoch ms) for the chips above — the EARLIEST
   * createdAtMs among that thread's running tasks, so a chip lit by discovery
   * shows the work's true age instead of restarting at 0:00 every time the
   * poll re-arms it. Absent for a thread whose tasks carry no usable
   * timestamp; the host then falls back to "now" as before.
   */
  chipEpochByThread: Record<string, number>;
  /**
   * Locally-cached in-flight messages missing a serverTaskId that should be
   * stamped so the existing resume path re-attaches them (cross-device edge
   * where the local placeholder lost the stamp). `isActive` routes the write:
   * active thread → updateStreamingMessageById; background → per-thread store.
   */
  stamps: Array<{ threadId: string; messageId: string; taskId: string; isActive: boolean }>;
}

function isInFlightAssistant(m: ResumeScanMessage): boolean {
  if (!m || m.isUser) return false;
  if (m.interrupted || m.status === 'interrupted') return false;
  if (m.status === 'complete') return false;
  const hasBody = !!((m.text || '').trim() || (m.html || '').trim());
  // A finalized answer has a real body and no pending status; an in-flight
  // placeholder is empty or explicitly streaming.
  return !hasBody || m.status === 'streaming' || m.status === undefined;
}

/** The most-recent in-flight assistant message in a thread, or null. */
function findInFlightAssistant(messages: ResumeScanMessage[]): ResumeScanMessage | null {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m && !m.isUser) {
      return isInFlightAssistant(m) ? m : null; // stop at the last assistant bubble
    }
  }
  return null;
}

/**
 * Phase 2 — honest background chips. Given the threads we previously lit from
 * discovery and the server's freshly-confirmed running set, decide which
 * discovery-lit chips to CLEAR (their task went terminal). Never clears the
 * active thread — its own finalize owns that, so a live in-flight turn is
 * never cut short. Pure; the host applies stopProcessing to `toClear`.
 */
export function reconcileDiscoveryChips(params: {
  previouslyLit: Iterable<string>;
  confirmed: Iterable<string>;
  activeThreadId: string | null;
}): { toClear: string[]; stillLit: string[] } {
  const confirmedSet = new Set(params.confirmed);
  const toClear: string[] = [];
  const stillLit: string[] = [];
  for (const threadId of params.previouslyLit) {
    if (confirmedSet.has(threadId)) {
      stillLit.push(threadId);
    } else if (threadId !== params.activeThreadId) {
      toClear.push(threadId);
    }
    // A no-longer-confirmed thread that IS active is neither cleared here nor
    // kept lit by discovery — its finalize path manages it.
  }
  return { toClear, stillLit };
}

/**
 * Turn the server's active-task list into a chip + stamp plan. Pure. One chip
 * per distinct thread that has a confirmed-running task; a stamp only when that
 * thread's locally-cached in-flight message exists but lacks the serverTaskId.
 */
export function planActiveTaskReattach(params: {
  activeTasks: ActiveTaskLike[];
  activeThreadId: string | null;
  getThreadMessages: (threadId: string) => ResumeScanMessage[];
}): ActiveTaskReattachPlan {
  const { activeTasks, activeThreadId, getThreadMessages } = params;
  const chipThreadIds: string[] = [];
  const seenChip = new Set<string>();
  const chipEpochByThread: Record<string, number> = {};
  const stamps: ActiveTaskReattachPlan['stamps'] = [];

  for (const task of activeTasks || []) {
    const threadId = (task?.threadId || '').trim();
    const taskId = (task?.taskId || '').trim();
    if (!threadId || !taskId) continue;

    if (!seenChip.has(threadId)) {
      seenChip.add(threadId);
      chipThreadIds.push(threadId);
    }

    // Earliest running task wins the thread's clock. Guard against a clock
    // skew or a bad stamp handing the chip a future start (which would render
    // a negative age) — those fall back to "now" at the host.
    const created = typeof task?.createdAtMs === 'number' ? task.createdAtMs : NaN;
    if (Number.isFinite(created) && created > 0 && created <= Date.now()) {
      const prior = chipEpochByThread[threadId];
      if (prior === undefined || created < prior) chipEpochByThread[threadId] = created;
    }

    let messages: ResumeScanMessage[] = [];
    try { messages = getThreadMessages(threadId) || []; } catch { messages = []; }
    const inFlight = findInFlightAssistant(messages);
    if (inFlight && inFlight.id && !inFlight.serverTaskId) {
      stamps.push({ threadId, messageId: inFlight.id, taskId, isActive: threadId === activeThreadId });
    }
  }

  return { chipThreadIds, chipEpochByThread, stamps };
}
